<script setup>
import GenerateProductFeedAction from '@frontJs/components/actions/projectManagement/sma/GenerateProductFeedAction.vue';
import ArrowsClockwiseIcon from '@iconify-vue/ph/arrows-clockwise';
import CopySimpleIcon from '@iconify-vue/ph/copy-simple';
import DownloadSimpleIcon from '@iconify-vue/ph/download-simple';
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import PlusIcon from '@iconify-vue/ph/plus';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import PillCell from '@js/components/tables/cells/PillCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { toastSuccess } from '@js/utils/toast';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { tenant } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
    updateData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'id',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'ID',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.id,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'type',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Type',
                }),
            cell: (props) =>
                h(PillCell, {
                    pillValue: props.row.original.type.label,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'name',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nom',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.name,
                    length: 160,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'url',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Flux',
                    width: 0,
                }),
            cell: (props) =>
                props.row.original.url
                    ? h(
                          BaseCell,
                          {},
                          {
                              default: () => [
                                  h(ActionButton, {
                                      href: props.row.original.url,
                                      target: '_blank',
                                      type: 'secondary',
                                      size: 'small',
                                      square: true,
                                      icon: DownloadSimpleIcon,
                                  }),
                                  h(ActionButton, {
                                      callback: () => {
                                          navigator.clipboard.writeText(
                                              props.row.original.url,
                                          );

                                          toastSuccess('Copié avec succès.');
                                      },
                                      target: '_blank',
                                      type: 'secondary',
                                      size: 'small',
                                      square: true,
                                      icon: CopySimpleIcon,
                                  }),
                              ],
                          },
                      )
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'generatedAt',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Généré le',
                }),
            cell: (props) =>
                props.row.original.generatedAt
                    ? h(TextCell, {
                          text: props.row.original.generatedAt,
                      })
                    : null,
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(ActionButton, {
                                size: 'small',
                                title: 'Modifier',
                                href: route(
                                    'front.projectManagement.sma.productFeed',
                                    {
                                        tenant: tenant.id,
                                        facebookAdsProductFeed:
                                            props.row.original.id,
                                    },
                                ),
                                icon: PencilSimpleIcon,
                                square: true,
                            }),
                            h(
                                GenerateProductFeedAction,
                                {
                                    tenant,
                                    facebookAdsProductFeed: props.row.original,
                                    callback: () => {
                                        updateData();
                                    },
                                },
                                {
                                    button: ({ action, loading }) =>
                                        h(ActionButton, {
                                            size: 'small',
                                            title: 'Générer',
                                            type: 'secondary',
                                            callback: () => action(),
                                            loading: loading,
                                            square: true,
                                            icon: ArrowsClockwiseIcon,
                                        }),
                                },
                            ),
                        ],
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.sma.postProductFeeds', {
        tenant: tenant.id,
    }),
    columnPinning: {
        right: ['actions'],
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
    >
        <template #actions>
            <ActionButton
                size="small"
                :href="
                    route('front.projectManagement.sma.productFeed', {
                        tenant: tenant.id,
                    })
                "
                :left-icon="PlusIcon"
            >
                Ajouter
            </ActionButton>
        </template>
    </DataTableLayout>
</template>
